import * as React from 'react';

import { createEditorDocument, diffDocuments } from '@gitbook/doc-core';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { Value } from '@gitbook/slate';
import { getSuspense } from '@gitbook/util-suspense-cache';

import { DiffContext } from '../Diff';

/**
 * Return the document value for the current page depending on the diff mode being used.
 * And it also returns the original document value for diffing.
 */
export function usePageDocumentDiffValue(input: { pageId: string; value: Value }): Value {
    const { pageId, value } = input;
    const { baseRevision, diffMode } = React.useContext(DiffContext);

    const hiveClient = useHiveClient();

    // Fetch the base document we are diffing against
    const baseDocument = React.useMemo(() => {
        // diffMode isn't on, so we don't need to diff
        if (!diffMode) {
            return null;
        }

        if (!baseRevision) {
            return null;
        }

        const contentId = baseRevision.contentId;
        const basePage = baseRevision.pages[pageId];

        if (!basePage || !hiveCore.isRevisionPageDocument(basePage)) {
            return null;
        }

        const baseDocumentId = basePage.document;

        // Base pages that exist but without a document yet are considered empty.
        if (!baseDocumentId) {
            return createEditorDocument('');
        }

        // Fetch the original document for diffing
        const baseDocument = getSuspense(hiveClient.store.getDocument(contentId, baseDocumentId));
        if (!baseDocument) {
            throw new Error(`Original page document "${baseDocumentId}" not found`);
        }

        return baseDocument;
    }, [diffMode, pageId, baseRevision, hiveClient.store]);

    const diffValue = React.useMemo(() => {
        if (!baseDocument) {
            return null;
        }

        return Value.create({ document: diffDocuments(baseDocument, value.document) });
    }, [baseDocument, value.document]);

    return diffValue ?? value;
}
